<template>
  <div ref="selectorRef" class="lang-selector">
    <button 
      class="lang-btn" 
      @click="toggle"
      @keydown.enter.prevent="onEnterOrSpace"
      @keydown.space.prevent="onEnterOrSpace"
      @keydown.escape="onEscape"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="$t('languageSelector.label', 'Вибір мови')"
    >
      <span class="lang-text">{{ current }}</span>
      <svg 
        class="chevron" 
        :class="{ open: isOpen }" 
        width="23" 
        height="19" 
        viewBox="0 0 24 24" 
        fill="none" 
        aria-hidden="true"
      >
        <path 
          d="M6 9L12 15L18 9" 
          stroke="currentColor" 
          stroke-width="2.5" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <Teleport to="body">
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95 -translate-y-2"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 -translate-y-2"
      >
        <div 
          v-if="isOpen" 
          class="lang-dropdown"
          :style="dropdownStyle"
          role="listbox"
          @keydown.escape="onEscape"
          @keydown.arrow-down.prevent="onNavigateDown"
          @keydown.arrow-up.prevent="onNavigateUp"
          @keydown.enter.prevent="onSelectFocused"
        >
          <button
            v-for="{ code, label } in others"
            :key="code"
            class="lang-option"
            :class="{ 
              focused: focusedIndex === others.findIndex(l => l.code === code) 
            }"
            role="option"
            :aria-selected="false"
            @click="select(code)"
            @mouseenter="focusedIndex = others.findIndex(l => l.code === code)"
          >
            {{ label }}
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { getLocaleEndonym } from '@/utils/locale'
import { useDropdown } from '@/composables/ui'
import { useLocaleSwitch } from '@/composables/locale'
import { useCourseStore } from '@/stores/course'

interface Props {
  currentLocale?: string
  fallbackLocale?: string
}

const props = withDefaults(defineProps<Props>(), {
  currentLocale: undefined,
  fallbackLocale: undefined
})

const emit = defineEmits<{
  localeChange: [locale: string]
}>()

const { setLocale, supportedLocales } = useLocaleSwitch()
const { globalLocales } = storeToRefs(useCourseStore())

const isOpen = ref(false)
const selectorRef = ref<HTMLElement | null>(null)
const focusedIndex = ref(-1)

/**
 * Определяет текущую локаль с приоритетом пропса над fallback локалью
 */
const effectiveLocale = computed(() => {
  if (props.currentLocale && supportedLocales.includes(props.currentLocale as any)) {
    return props.currentLocale
  }
  return props.fallbackLocale || 'en'
})

/**
 * Список мов для хедера:
 * - якщо globalLocales вже завантажені — показуємо тільки мови з курсами
 * - поки завантажуються — fallback на всі supportedLocales
 */
const SITE_LOCALES = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
]

const languages = computed(() => SITE_LOCALES)
const current = computed(() => getLocaleEndonym(effectiveLocale.value))
const others = computed(() => languages.value.filter((l) => l.code !== effectiveLocale.value))

// Используем composable для управления dropdown с адаптивной высотой
const { dropdownStyle } = useDropdown(isOpen, selectorRef, {
  triggerSelector: '.lang-selector',
  dropdownSelector: '.lang-dropdown',
  dropdownHeight: 200,
  offset: 12,
  itemHeight: 44, // Высота одного элемента (padding 8px сверху + 8px снизу + ~28px текста)
  minHeight: 100,
  maxHeight: 400,
})

const toggle = () => {
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    focusedIndex.value = -1
  }
}

const close = () => {
  isOpen.value = false
  focusedIndex.value = -1
}

// Обёртки для обработчиков с модификаторами — всегда передаём функцию, чтобы избежать
// "Cannot read properties of undefined (reading '_withMods')" при гидрации/ре-рендере.
const onEnterOrSpace = () => toggle()
const onEscape = () => close()
const onNavigateDown = () => navigateDown()
const onNavigateUp = () => navigateUp()
const onSelectFocused = () => selectFocused()

const select = async (code: string) => {
  await setLocale(code)
  emit('localeChange', code)
  close()
}

const navigateDown = () => {
  if (focusedIndex.value < others.value.length - 1) {
    focusedIndex.value++
  } else {
    focusedIndex.value = 0
  }
}

const navigateUp = () => {
  if (focusedIndex.value > 0) {
    focusedIndex.value--
  } else {
    focusedIndex.value = others.value.length - 1
  }
}

const selectFocused = () => {
  if (focusedIndex.value >= 0 && focusedIndex.value < others.value.length) {
    const option = others.value[focusedIndex.value]
    if (option) {
      select(option.code)
    }
  }
}
</script>

<style scoped>
.lang-selector {
  position: relative;
}

.lang-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  transition: opacity 0.2s;
}

.lang-btn:hover {
  opacity: 0.8;
}

.lang-btn:focus-visible {
  outline: 2px solid #252840;
  outline-offset: 2px;
  border-radius: 4px;
}

.lang-text {
  font-family: var(--font-inter);
  font-weight: 500;
  font-size: 16px;
  color: #252840;
}

.chevron {
  color: #252840;
  transition: transform 0.2s ease-out;
  flex-shrink: 0;
}

.chevron.open {
  transform: rotate(180deg);
}

.lang-dropdown {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  padding: 12px 0;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 80px;
  /* Адаптивная высота управляется через maxHeight в dropdownStyle */
}

/* Кастомный скроллбар для dropdown */
.lang-dropdown::-webkit-scrollbar {
  width: 6px;
}

.lang-dropdown::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.lang-dropdown::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.lang-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.lang-option {
  display: block;
  width: 100%;
  padding: 8px 20px;
  text-align: left;
  background: transparent;
  border: none;
  color: #252840;
  font-family: var(--font-inter);
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
  outline: none;
  white-space: nowrap;
}

.lang-option:hover,
.lang-option.focused {
  color: #252840;
  background-color: rgba(0, 0, 0, 0.05);
}

.lang-option:focus-visible {
  outline: 2px solid #252840;
  outline-offset: -2px;
  border-radius: 4px;
}
</style>

